/**
 * Custom Login Page Component
 * Server-side password validation with role-based auto-routing
 * Based on architecture document specifications
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, AlertCircle, X } from 'lucide-react';
import useLogin from '../../hooks/useLogin';
import { validateLoginForm } from '../../utils/validation';
import bilingualToast from '../../utils/bilingualToast';
import ZohoLoginButton from '../../components/auth/ZohoLoginButton';
import api, { AUTH_MISMATCH_REASON_KEY } from '../../redux/api';
import { setAuthenticated } from '../../redux/features/adminSlice';
import '../../styles/auth/CustomLoginPage.css';
import loginImage from '../../img/loginImage.webp';
import I18nReadyGuard from '../../i18n/components/I18nReadyGuard';

/**
 * Where a signed-in user belongs, per role. Mirrors the backend's
 * `buildRedirectUrl` so a restored session lands on the same portal the user
 * was originally sent to after signing in.
 */
const ROLE_HOME = {
  1: '/admin/dashboard',
  2: '/admin/dashboard',
  3: '/seller/dashboard',
  4: '/cashier/pos',
  5: '/cashier/pos',
};

/**
 * CustomLoginPage Component
 * Implements custom login form with server-side validation
 */
const CustomLoginPage = () => {
  const { t, i18n } = useTranslation('login');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.loyalty.admin);

  // A VALID session cookie may still be present — the user closed the tab and
  // came back, or the 30-day "remember me" session is still alive. App.js
  // deliberately SKIPS the session check on public paths like /login (see its
  // `isPublicPath && !token` early return), so the login page has to ask the
  // backend itself. Without this, a signed-in user is asked to sign in again
  // even though their session is still perfectly valid.
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        const res = await api.get('/auth/status', { withCredentials: true });
        if (cancelled) return;

        const data = res && res.data;
        if (!data || data.status !== 'success' || !data.data || !data.data.user) return;

        const user = data.data.user;
        dispatch(setAuthenticated(user));
        navigate(ROLE_HOME[user.role_id] || '/admin/dashboard', { replace: true });
      } catch (e) {
        // No valid session — showing the login form is the correct outcome.
      }
    };

    restoreSession();
    return () => { cancelled = true; };
  }, [dispatch, navigate]);

  // ... existing code ...

  /**
   * Handle language change
   */
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    // Change document direction based on language
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  };

  // Wrap entire component with I18nReadyGuard to prevent rendering before translations load
  return (
    <I18nReadyGuard namespaces={['common', 'login']}>
      <LoginPageContent
        t={t}
        i18n={i18n}
        navigate={navigate}
        isAuthenticated={isAuthenticated}
        handleLanguageChange={handleLanguageChange}
      />
    </I18nReadyGuard>
  );
};

const REMEMBER_ME_STORAGE_KEY = 'rememberMe';

/**
 * Read the persisted "remember me" preference. Storage can be unavailable
 * (private mode / blocked cookies), so never let it break the login page.
 */
const getStoredRememberMe = () => {
  try {
    return localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Inner component that contains the login page UI
 * Separated to keep I18nReadyGuard logic clean
 */
const LoginPageContent = ({ t, i18n, navigate, isAuthenticated, handleLanguageChange }) => {
  // Form state — rememberMe is persisted so the box stays ticked on return visits
  const [formData, setFormData] = useState(() => ({
    email: '',
    password: '',
    rememberMe: getStoredRememberMe(),
  }));

  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Forced re-login reason — written by the api response interceptor when the
  // backend rejects with ACCOUNT_CONTEXT_MISMATCH (the session belongs to a
  // DIFFERENT tenant than the host the user was trying to reach).
  const [mismatchReason, setMismatchReason] = useState(null);

  // Consume the reason exactly once, then clear it so a later visit is clean.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(AUTH_MISMATCH_REASON_KEY);
      if (raw) {
        setMismatchReason(JSON.parse(raw));
        sessionStorage.removeItem(AUTH_MISMATCH_REASON_KEY);
      }
    } catch (e) {
      // malformed or unavailable storage (private mode) — ignore
    }
  }, []);

  // Use login hook (set useMock: true for development without backend)
  const { login, loading, error, clearError } = useLogin({ useMock: false });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Persist the "remember me" preference so the box stays ticked on return visits
    if (name === 'rememberMe') {
      try {
        localStorage.setItem(REMEMBER_ME_STORAGE_KEY, String(checked));
      } catch (err) {
        // storage unavailable — non-fatal, the checkbox still works for this session
      }
    }

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    // Validate form
    const validation = validateLoginForm(formData, t);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      return;
    }

    try {
      // Attempt login — forward the "remember me" choice. The backend decides whether
      // the long-lived session is actually granted (account-level users only) and
      // reports the outcome as `rememberMeApplied` in the response.
      const response = await login(formData.email, formData.password, formData.rememberMe);

      // Honest feedback: the checkbox must never be a silent lie. This page unmounts as
      // soon as `isAuthenticated` flips (App.js swaps to the authenticated route tree),
      // so an inline notice here would never be seen — the app-root ToastContainer
      // (App.js) survives the route swap, so the outcome is shown there instead.
      if (formData.rememberMe) {
        if (response?.rememberMeApplied === false) {
          // Ticked the box, but the backend did not grant it (sub-sellers / cashiers).
          bilingualToast.warning(t('rememberMeNotApplied'), t('rememberMeNotApplied'));
        } else if (response?.rememberMeApplied === true) {
          // Long-lived session actually issued.
          bilingualToast.success(t('rememberMeApplied'), t('rememberMeApplied'));
        }
      }

      // Navigation handled by useLogin hook based on role_id
    } catch (err) {
      // Error handled by useLogin hook
      console.error('[CustomLoginPage] Login error:', err);
    }
  };

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  /**
   * Get subdomain for debugging (optional)
   */
  const getSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    return parts[0];
  };

  return (
    <div className="custom-login-page">
      {/* Header */}
      <header className="custom-login-header">
        <Link to="/" className="custom-login-logo">
          <img
            src="/promotly-logo.webp"
            alt="Promotly Logo"
            className="custom-login-logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'inline');
            }}
          />
          <span className="custom-login-logo-text" style={{ display: 'none' }}>Promotly</span>
        </Link>

        {/* Language Selector - Segmented Control */}
        <div className="custom-login-language-segmented">
          <button
            onClick={() => handleLanguageChange('en')}
            className={`lang-segment ${i18n.language === 'en' ? 'active' : ''}`}
            title={t('tooltips:switchToEnglish')}
          >
            English
          </button>
          <button
            onClick={() => handleLanguageChange('ar')}
            className={`lang-segment ${i18n.language === 'ar' ? 'active' : ''}`}
            title="التبديل إلى العربية"
          >
            العربية
          </button>
        </div>
      </header>

      {/* Main Container */}
      <div className="custom-login-container">
        {/* Left Column - Form Section */}
        <div className="custom-login-form-section">
          <div className="custom-login-form-wrapper">
            {/* Title */}
            <h1 className="custom-login-title">{t('welcomeBack')}</h1>
            <p className="custom-login-subtitle">{t('loginToYourAccount')}</p>

            {/* Account-context mismatch notice (forced re-login) */}
            {mismatchReason && (
              <div className="custom-login-mismatch-alert" role="status">
                <AlertCircle className="custom-login-mismatch-icon" size={18} />
                <span>
                  {mismatchReason.subdomain
                    ? t('tenantMismatchNoticeFor', { tenant: mismatchReason.subdomain })
                    : t('tenantMismatchNotice')}
                </span>
                <button
                  className="custom-login-mismatch-dismiss"
                  onClick={() => setMismatchReason(null)}
                  aria-label="Dismiss notice"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="custom-login-error-alert" role="alert">
                <AlertCircle className="custom-login-error-icon" size={18} />
                <span>{error}</span>
                <button
                  className="custom-login-error-dismiss"
                  onClick={clearError}
                  aria-label="Dismiss error"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* Login Form */}
            <form className="custom-login-form" onSubmit={handleSubmit} noValidate>
              {/* Email Field */}
              <div className="custom-login-form-group">
                <label htmlFor="email" className="custom-login-label">
                  {t('emailAddress')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className={`custom-login-input ${fieldErrors.email ? 'error' : ''}`}
                  placeholder={t('enterYourEmail')}
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="email"
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                  required
                />
                {fieldErrors.email && (
                  <span id="email-error" className="custom-login-field-error" role="alert">
                    {fieldErrors.email}
                  </span>
                )}
              </div>

              {/* Password Field */}
              <div className="custom-login-form-group">
                <label htmlFor="password" className="custom-login-label">
                  {t('password')}
                </label>
                <div className="custom-login-password-wrapper">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`custom-login-input ${fieldErrors.password ? 'error' : ''}`}
                    placeholder={t('enterYourPassword')}
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="current-password"
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="custom-login-password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <span id="password-error" className="custom-login-field-error" role="alert">
                    {fieldErrors.password}
                  </span>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="custom-login-links">
                <label className="custom-login-remember">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    className="custom-login-checkbox"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="custom-login-checkbox-label">{t('rememberMeFor30Days')}</span>
                </label>

                <Link to="/forgot-password" className="custom-login-link">
                  {t('forgotPassword')}
                </Link>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className="custom-login-button"
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span className="custom-login-spinner" aria-hidden="true"></span>
                    <span>{t('loggingIn')}</span>
                  </>
                ) : (
                  <span>{t('login')}</span>
                )}
              </button>
            </form>

            {/* Social Login Section */}
            <div className="custom-login-social-divider">
              <span className="custom-login-divider-line"></span>
              <span className="custom-login-divider-text">{t('or')}</span>
              <span className="custom-login-divider-line"></span>
            </div>

            <div className="custom-login-social-section">
              <ZohoLoginButton />
            </div>

            {/* Footer Links */}
            <div className="custom-login-footer">
              <p>
                {t('dontHaveAccount')}{' '}
                <Link to="/new-tenant-signup" state={{ from: 'login' }} className="custom-login-link">
                  {t('signUp')}
                </Link>
              </p>
            </div>

            {/* Debug Info (Development Only) */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ marginTop: '20px', fontSize: '11px', color: '#bbb', opacity: 0.7 }}>
                <p>Subdomain: {getSubdomain()}</p>
                <p>Environment: {process.env.NODE_ENV}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Welcome Section */}
        <div className="custom-login-welcome-section">
          <img
            src={loginImage}
            alt="Welcome to Promotly"
            className="custom-login-welcome-image"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
};

export default CustomLoginPage;